import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { paymentBankApi } from '../../api/paymentBank.js';
import { Spinner, ErrorBox } from '../../components/ui.js';
import { money } from '../../lib/format.js';

/**
 * 입금엑셀 확인 — 원본 payment/bank_check.php.
 *
 * "계좌이체양식 다운로드"로 받은 파일을 은행에 넣고 처리한 뒤, 그 파일 내용을
 * (은행코드\t계좌번호\t금액\t\t예금주 형식으로) 그대로 붙여넣으면 은행코드가
 * bank_code 표에 있는 코드인지만 확인해준다. DB 를 보지 않는 순수 검증이라
 * 서버 쓰기가 없다 — bank_code 목록만 받아 브라우저에서 대조한다.
 */

interface CheckedRow {
  bankcode: string;
  bankname: string;
  banksn: string;
  amount: string;
  bankinname: string;
  ok: boolean;
}

export function BankCheckPage() {
  const [draft, setDraft] = useState('');
  const [rows, setRows] = useState<CheckedRow[] | null>(null);

  const codes = useQuery({
    queryKey: ['payment-bank', 'codes'],
    queryFn: () => paymentBankApi.codes(),
    staleTime: 10 * 60 * 1000,
  });

  const nameByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of codes.data ?? []) m.set(c.code, c.bankname);
    return m;
  }, [codes.data]);

  function runCheck() {
    const lines = draft.split('\n').map((l) => l.trim()).filter(Boolean);
    setRows(
      lines.map((line) => {
        const cols = line.split('\t');
        const bankcode = cols[0] ?? '';
        const bankname = nameByCode.get(bankcode) ?? '';
        return {
          bankcode,
          bankname,
          banksn: cols[1] ?? '',
          amount: cols[2] ?? '',
          bankinname: cols[4] ?? '',
          ok: Boolean(bankname),
        };
      }),
    );
  }

  return (
    <section>
      <header className="page-head">
        <div>
          <Link className="link link--back" to="/reports/payment-bank">
            ← 입금건 관리
          </Link>
          <h1 className="page-title">입금엑셀 확인</h1>
          <p className="page-desc">
            은행 처리 후 받은 파일(계좌이체양식과 같은 탭 구분 형식)을 그대로 붙여넣으면 은행코드가
            맞는지만 확인합니다. DB 는 바꾸지 않습니다.
          </p>
        </div>
      </header>

      {codes.isPending ? (
        <Spinner />
      ) : codes.isError ? (
        <ErrorBox error={codes.error} onRetry={() => void codes.refetch()} />
      ) : (
        <>
          <textarea
            className="input input--area"
            style={{ height: 240, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
            placeholder="은행코드	계좌번호	금액		예금주"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div style={{ marginTop: 10 }}>
            <button type="button" className="btn btn--primary" onClick={runCheck}>
              체크하기
            </button>
          </div>

          {rows && (
            <div className="table-wrap" style={{ marginTop: 18 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>입력코드</th>
                    <th>은행코드</th>
                    <th>계좌번호</th>
                    <th style={{ textAlign: 'right' }}>금액</th>
                    <th>예금주</th>
                    <th>체크결과</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="td-muted" style={{ textAlign: 'center' }}>
                        붙여넣은 내용이 없습니다.
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, i) => (
                      <tr key={i}>
                        <td>{r.bankcode}</td>
                        <td>{r.bankname || <span className="td-muted">-</span>}</td>
                        <td>{r.banksn}</td>
                        <td className="td-right">{money(Number(r.amount.replace(/,/g, '')) || 0)}</td>
                        <td>{r.bankinname}</td>
                        <td>
                          {r.ok ? (
                            <span className="badge">OK</span>
                          ) : (
                            <span className="badge badge--danger">은행코드오류</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
